import { useEffect, useRef, useState } from "react";
import type { LatLng } from "@strava/feature-global-wasm-mre";

import createNetworkingClient from "../../utils/networking-client";
import { logError } from "../../utils/sentry";
import { isFinished } from "./blipClassName";
// LEGACY BRIDGE — see legacyBeaconBridge.ts for context. Remove these imports
// and the calls below when the CoffeeScript Beacon views are modernized.
import {
  notifyLegacyBeaconActivity,
  notifyLegacyBeaconActivityFailedSync,
} from "./legacyBeaconBridge";

interface ActivityUpdate {
  streams?: { latlng?: LatLng[]; timestamp?: number[] };
  status?: number;
  update_time?: number;
}

interface UseBeaconPollingOptions {
  beaconId: string;
  initialPolyline: LatLng[];
  initialStatus: number;
  initialUpdateTime?: number;
  pollingInterval: number;
}

interface UseBeaconPollingResult {
  livePolyline: LatLng[];
  status: number;
}

const api = createNetworkingClient();

function useBeaconPolling({
  beaconId,
  initialPolyline,
  initialStatus,
  initialUpdateTime,
  pollingInterval,
}: UseBeaconPollingOptions): UseBeaconPollingResult {
  const [livePolyline, setLivePolyline] = useState<LatLng[]>(initialPolyline);
  const [status, setStatus] = useState(initialStatus);
  // Seed with the server-reported update_time so the first poll sends
  // minimum_timestamp and returns only the delta — otherwise the server
  // returns the full stream and we'd append duplicates to the polyline.
  const updateTimeRef = useRef<number | undefined>(initialUpdateTime);
  // Skip ticks while a previous poll is still in flight. Without this, a slow
  // response — most often from a sketchy client connection — can stack up to
  // 6 overlapping requests before any resolve (axios timeout is 30s, polling
  // interval 5s). Mirrors the legacy LiveActivity.update guard.
  const pollInFlightRef = useRef(false);

  const finished = isFinished(status);

  useEffect(() => {
    if (finished || pollingInterval <= 0) return undefined;

    const poll = async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;

      const params =
        updateTimeRef.current !== undefined
          ? { minimum_timestamp: updateTimeRef.current }
          : {};

      try {
        const { data } = await api.get<ActivityUpdate>(`/beacon/${beaconId}`, {
          params,
        });

        if (data.update_time !== undefined) {
          updateTimeRef.current = data.update_time;
        }
        if (data.status !== undefined) {
          setStatus(data.status);
        }
        const newPoints = data.streams?.latlng;
        if (newPoints && newPoints.length > 0) {
          setLivePolyline((prev) => [...prev, ...newPoints]);
        }
        // LEGACY BRIDGE — feed the response into the Backbone LiveActivity
        // so StatsView/AvatarView/BeaconView keep updating. Delete with the
        // CoffeeScript modernization.
        notifyLegacyBeaconActivity(data);
      } catch (error) {
        // Non-fatal — the next poll will retry. Log so we can see persistent failures.
        logError(error);
        // LEGACY BRIDGE — fire the legacy `failedsync` event so the
        // connection-err / not-available alert still appears. Delete with
        // the CoffeeScript modernization.
        notifyLegacyBeaconActivityFailedSync(error);
      } finally {
        pollInFlightRef.current = false;
      }
    };

    const intervalId = setInterval(poll, pollingInterval);
    return () => clearInterval(intervalId);
  }, [beaconId, pollingInterval, finished]);

  return { livePolyline, status };
}

export default useBeaconPolling;
